import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarPlus, ChevronLeft, ChevronRight, Loader2, PartyPopper, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { api, errMsg, fmtDay, istDateISO } from "../../lib/api";
import { ReasonDialog } from "./ReasonDialog";
import { WEEKDAYS, dayISO, daysInMonth, monthLabel, shiftMonth, weekdayOf } from "./shared";

/** Add / edit holiday dialog. */
function HolidayDialog({ code, open, onOpenChange, holiday, defaultDay, onSaved }) {
  const [form, setForm] = useState({ day: "", name: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ day: holiday?.day || defaultDay || istDateISO(), name: holiday?.name || "", note: holiday?.note || "" }); }, [open, holiday, defaultDay]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (holiday) {
        await api.put(`/t/${code}/attendance/holidays/${holiday.id}`, form);
        toast.success(`Holiday updated: ${form.name} on ${fmtDay(form.day)}`);
      } else {
        await api.post(`/t/${code}/attendance/holidays`, form);
        toast.success(`Holiday added: ${form.name} on ${fmtDay(form.day)}`, { description: "All collectors and supervisors have been notified in-app." });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="holiday-dialog">
        <form onSubmit={submit} className="space-y-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><PartyPopper className="h-5 w-5 text-primary" aria-hidden="true" /> {holiday ? "Edit holiday" : "Add holiday"}</DialogTitle>
            <DialogDescription>No attendance is recorded on a holiday; the day shows as Holiday in every collector's calendar. Future months are fine. Staff are notified.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={form.day} onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))} required className="h-10" data-testid="holiday-date-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required maxLength={120} placeholder="e.g. Diwali" className="h-10" data-testid="holiday-name-input" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Note (optional)</Label><Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} maxLength={500} data-testid="holiday-note-input" /></div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="holiday-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy || form.name.trim().length < 2 || !form.day} data-testid="holiday-save-button">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {holiday ? "Save changes" : "Add holiday"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Phase 168 — Holiday management: month calendar (Sundays = weekly off, holidays, working Sundays) + list with add /
 * edit / delete, and "make this Sunday a working day" (mandatory reason). Read-only when `readOnly`.
 */
export function HolidaysTab({ code, readOnly = false, onChanged }) {
  const today = istDateISO();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);      // {holiday?, defaultDay?}
  const [del, setDel] = useState(null);
  const [sunday, setSunday] = useState(null);      // {day, on}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/t/${code}/attendance/holidays`, { params: { month } }).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [code, month]);
  useEffect(() => { load(); }, [load]);
  const byDay = Object.fromEntries((data?.items || []).map((h) => [h.day, h]));

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/t/${code}/attendance/holidays/${del.id}`);
      toast.success(`Holiday removed: ${del.name} on ${fmtDay(del.day)}`, { description: "Staff have been notified; normal attendance applies that day." });
      setDel(null); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggleSunday = async (reason) => {
    try {
      await api.post(`/t/${code}/attendance/working-sunday`, { day: sunday.day, on: sunday.on, reason });
      toast.success(sunday.on ? `Sunday ${fmtDay(sunday.day)} is now a working day` : `Sunday ${fmtDay(sunday.day)} is a weekly off again`);
      setSunday(null); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const first = weekdayOf(dayISO(month, 1));
  const total = daysInMonth(month);
  const cells = [...Array(first).fill(null), ...Array.from({ length: total }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const holidays = (data?.items || []).filter((h) => h.type === "holiday");
  const workingSundays = (data?.items || []).filter((h) => h.type === "working_sunday");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-3" data-testid="holidays-tab">
      <Card className="shadow-sm">
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month" data-testid="holidays-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <h3 className="text-base font-semibold font-heading min-w-[180px] text-center" data-testid="holidays-month-label">{monthLabel(month)}</h3>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month" data-testid="holidays-next-month"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => setMonth(today.slice(0, 7))} data-testid="holidays-this-month">This month</Button>
            <div className="ml-auto flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-muted-foreground/30" /> Sunday (weekly off)</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-violet-300" /> Holiday</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[hsl(var(--warning))]/60" /> Working Sunday</span>
            </div>
            {!readOnly && <Button size="sm" className="h-9" onClick={() => setDialog({ defaultDay: month === today.slice(0, 7) ? today : dayISO(month, 1) })} data-testid="add-holiday-button"><CalendarPlus className="h-4 w-4 mr-1.5" /> Add holiday</Button>}
          </div>
          {error && <p className="text-xs text-destructive" data-testid="holidays-error">{error}</p>}
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground">{WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}</div>
          <div className="grid grid-cols-7 gap-1" data-testid="holidays-calendar">
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} className="min-h-[64px]" />;
              const iso = dayISO(month, d);
              const h = byDay[iso];
              const isSun = weekdayOf(iso) === 6;
              const kind = h?.type === "holiday" ? "holiday" : h?.type === "working_sunday" ? "working_sunday" : isSun ? "sunday" : "working";
              const cls = { holiday: "bg-violet-50 border-violet-200", working_sunday: "bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30", sunday: "bg-muted/60 border-border", working: "bg-background border-border hover:bg-muted/40" }[kind];
              const canClick = !readOnly && (kind === "working" || kind === "sunday" || kind === "working_sunday" || kind === "holiday");
              const onClick = () => {
                if (readOnly) return;
                if (kind === "holiday") setDialog({ holiday: h });
                else if (kind === "sunday") setSunday({ day: iso, on: true });
                else if (kind === "working_sunday") setSunday({ day: iso, on: false });
                else setDialog({ defaultDay: iso });
              };
              return (
                <button key={iso} type="button" onClick={onClick} disabled={!canClick} data-testid={`holiday-cell-${iso}`} data-kind={kind}
                  className={`min-h-[64px] rounded-md border p-1.5 text-left flex flex-col transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${cls} ${iso === today ? "ring-2 ring-primary/60" : ""} ${canClick ? "cursor-pointer" : "cursor-default"}`}
                  title={h ? (h.type === "holiday" ? `${h.name}${readOnly ? "" : " — click to edit"}` : `Working Sunday: ${h.reason}`) : isSun ? (readOnly ? "Weekly off" : "Weekly off — click to make it a working day") : (readOnly ? "" : "Click to add a holiday")}>
                  <span className={`text-xs font-medium tabular-nums ${iso === today ? "text-primary" : ""}`}>{d}</span>
                  {kind === "holiday" && <span className="mt-auto text-[10px] leading-tight text-violet-800 line-clamp-2">{h.name}</span>}
                  {kind === "sunday" && <span className="mt-auto text-[10px] text-muted-foreground">Weekly off</span>}
                  {kind === "working_sunday" && <span className="mt-auto text-[10px] text-[hsl(var(--warning))] font-medium">Working day</span>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <Card className="shadow-sm">
          <CardContent className="p-3">
            <p className="text-sm font-medium flex items-center gap-2 mb-2"><PartyPopper className="h-4 w-4 text-primary" aria-hidden="true" /> Holidays in {monthLabel(month)} <Badge variant="secondary" className="text-[10px]" data-testid="holidays-count">{holidays.length}</Badge></p>
            {holidays.length === 0 ? <p className="text-xs text-muted-foreground py-3" data-testid="holidays-empty">No holidays declared this month.{!readOnly && " Click a day in the calendar or use “Add holiday”."}</p> : (
              <ul className="divide-y rounded-md border" data-testid="holidays-list">
                {holidays.map((h) => (
                  <li key={h.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`holiday-row-${h.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{h.name}</p>
                      <p className="text-xs text-muted-foreground">{fmtDay(h.day)} · {WEEKDAYS[weekdayOf(h.day)]}{h.note ? ` · ${h.note}` : ""}</p>
                    </div>
                    {!readOnly && (<>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDialog({ holiday: h })} aria-label={`Edit ${h.name}`} data-testid={`holiday-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDel(h)} aria-label={`Delete ${h.name}`} data-testid={`holiday-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>)}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-3">
            <p className="text-sm font-medium flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" /> Working Sundays <Badge variant="secondary" className="text-[10px]" data-testid="working-sundays-count">{workingSundays.length}</Badge></p>
            <p className="text-xs text-muted-foreground mb-2">Every Sunday is a compulsory weekly off. {readOnly ? "The admin can" : "Click a Sunday in the calendar to"} declare it a working day (reason required) — attendance is then tracked normally.</p>
            {workingSundays.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="working-sundays-empty">No working Sundays this month.</p> : (
              <ul className="divide-y rounded-md border" data-testid="working-sundays-list">
                {workingSundays.map((h) => (
                  <li key={h.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`working-sunday-row-${h.day}`}>
                    <div className="min-w-0 flex-1"><p className="font-medium">{fmtDay(h.day)}</p><p className="text-xs text-muted-foreground">{h.reason} · by {h.created_by_name}</p></div>
                    {!readOnly && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setSunday({ day: h.day, on: false })} data-testid={`working-sunday-revert-${h.day}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Revert</Button>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <HolidayDialog code={code} open={!!dialog} onOpenChange={(v) => !v && setDialog(null)} holiday={dialog?.holiday} defaultDay={dialog?.defaultDay} onSaved={() => { load(); onChanged?.(); }} />
      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent data-testid="holiday-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Remove holiday “{del?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>{del && fmtDay(del.day)} becomes a normal working day again and attendance applies. Every collector and supervisor is notified.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="holiday-delete-cancel">Keep holiday</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="holiday-delete-confirm">{busy ? "Removing…" : "Remove holiday"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReasonDialog open={!!sunday} onOpenChange={(v) => !v && setSunday(null)} icon={AlertTriangle}
        title={sunday?.on ? `Make Sunday ${sunday ? fmtDay(sunday.day) : ""} a working day` : `Revert Sunday ${sunday ? fmtDay(sunday.day) : ""} to a weekly off`}
        description={sunday?.on ? "Attendance will be tracked normally for every collector on this Sunday. All staff are notified with your reason." : "The Sunday becomes a weekly off again; any attendance recorded for it is cleared."}
        confirmLabel={sunday?.on ? "Make working day" : "Revert to weekly off"} onConfirm={toggleSunday} testId="working-sunday-dialog" />
    </div>
  );
}
